import { useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Maximize2 } from "lucide-react";
import PhotoLightbox from "./PhotoLightbox";

/**
 * 4-page FAQ flyer carousel:
 * - Prev/next arrows + tap dots
 * - Swipe on touch
 * - Tap to open full-screen (PhotoLightbox with pinch/zoom)
 */
export default function FaqCarousel({ pages, animalName }) {
  const [index, setIndex] = useState(0);
  const [lightbox, setLightbox] = useState(null);
  const touchStart = useRef(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  const valid = (pages || []).filter(Boolean);
  const total = valid.length;

  if (total === 0) {
    return (
      <div
        data-testid="faq-empty"
        className="w-full aspect-[8.5/11] bg-[#F5EFE1] border border-dashed border-[#A87149]/50 rounded-md flex items-center justify-center text-[#6D5A50] font-body text-sm px-6 text-center"
      >
        FAQ flyer coming soon.
      </div>
    );
  }

  const go = (i) => setIndex(Math.max(0, Math.min(total - 1, i)));
  const cur = valid[index];
  const curFull = cur.startsWith("http") ? cur : `${backendUrl}${cur}`;

  const onTouchStart = (e) => { touchStart.current = e.touches[0]; };
  const onTouchEnd = (e) => {
    if (!touchStart.current) return;
    const dx = e.changedTouches[0].clientX - touchStart.current.clientX;
    if (Math.abs(dx) > 50) {
      if (dx < 0) go(index + 1);
      else go(index - 1);
    }
    touchStart.current = null;
  };

  return (
    <>
      <div className="relative">
        <div
          data-testid="faq-carousel"
          className="relative w-full aspect-[8.5/11] bg-[#F5EFE1] border border-[#A87149]/40 rounded-md overflow-hidden page-shadow group"
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <button
            type="button"
            onClick={() => setLightbox(index)}
            className="absolute inset-0 w-full h-full"
            aria-label={`Open FAQ page ${index + 1} in full screen`}
          >
            <img
              src={curFull}
              alt={`${animalName} FAQ page ${index + 1} of ${total}`}
              className="w-full h-full object-contain"
              loading="lazy"
            />
          </button>
          <span className="absolute top-3 right-3 bg-black/50 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1 pointer-events-none">
            <Maximize2 size={12} /> tap to expand
          </span>
          {total > 1 && (
            <span
              data-testid="faq-page-counter"
              className="absolute top-3 left-3 bg-black/50 text-white text-xs px-2 py-1 rounded-full pointer-events-none"
            >
              Page {index + 1} of {total}
            </span>
          )}

          {total > 1 && index > 0 && (
            <button
              data-testid="faq-prev"
              onClick={(e) => { e.stopPropagation(); go(index - 1); }}
              aria-label="Previous FAQ page"
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/85 hover:bg-white shadow rounded-full p-2 transition"
            >
              <ChevronLeft size={20} className="text-[#4B3A2F]" />
            </button>
          )}
          {total > 1 && index < total - 1 && (
            <button
              data-testid="faq-next"
              onClick={(e) => { e.stopPropagation(); go(index + 1); }}
              aria-label="Next FAQ page"
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/85 hover:bg-white shadow rounded-full p-2 transition"
            >
              <ChevronRight size={20} className="text-[#4B3A2F]" />
            </button>
          )}
        </div>

        {total > 1 && (
          <div className="flex items-center justify-center gap-2 mt-3" data-testid="faq-dots">
            {valid.map((_, i) => (
              <button
                key={i}
                onClick={() => go(i)}
                aria-label={`Go to page ${i + 1}`}
                className={`h-2.5 rounded-full transition-all ${i === index ? "w-6 bg-[#4B3A2F]" : "w-2.5 bg-[#A87149]/40 hover:bg-[#A87149]/70"}`}
              />
            ))}
          </div>
        )}
      </div>

      {lightbox !== null && (
        <PhotoLightbox
          items={valid.map((u, i) => ({ url: u, caption: `${animalName} — FAQ page ${i + 1}` }))}
          index={lightbox}
          onClose={() => setLightbox(null)}
          onNavigate={setLightbox}
        />
      )}
    </>
  );
}
